import 'ol/ol.css'
import './locations_map.css';

import Map from 'ol/Map';
import View from 'ol/View';

import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';

import OSM from 'ol/source/OSM';
import VectorSource from 'ol/source/Vector';

import GeoJSON from 'ol/format/GeoJSON';

import { Fill, Stroke, Style, Icon, Text, Circle } from 'ol/style';
import { OverviewMap, defaults as defaultControls } from 'ol/control';

import { defaults as defaultInteractions, DragPan, MouseWheelZoom } from 'ol/interaction';
import { primaryAction, platformModifierKeyOnly } from 'ol/events/condition';

import { transform } from 'ol/proj';

import Cluster from 'ol/source/Cluster';

function initLocationsMap() {
    // -----------------------------
    // DOM References
    // -----------------------------
    const mapbox = document.getElementById("mapbox");

    // -----------------------------
    // Constants & Configurations
    // -----------------------------
    const ICON_BY_ENTITY_TYPE = {
        QuickDrawBranch: "/img/qdpin.png",
        AuthDealer: "/img/dealerpin.png",
        AuthRepair: "/img/repairpin.png",
    };

    const LABELS_BY_LANG = {
        0: {  // English
            phone: "Phone:",
            website: "Website:",
            hours: "Office Hours",
            maps: "Show on Google maps...",
            days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
        },
        1: {  // French
            phone: "Téléphone:",
            website: "Site web:",
            hours: "Heures de bureau",
            maps: "Afficher sur Google maps...",
            days: ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"]
        },
        2: {  // Spanish
            phone: "Teléfono:",
            website: "Sitio web:",
            hours: "Horario de oficina",
            maps: "Mostrar en Google maps...",
            days: ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"]
        },
    };

    // -----------------------------
    // Helpers
    // -----------------------------
    function getPageLangIndex() {
        const lang = document.documentElement.lang || "en";

        if (lang === "fr") return 1;
        if (lang === "es") return 2;
        return 0;  // Default: English
    }

    function getDayValue(dayField, langIndex) {
        // Get value by language index from dayField array (safe fallback if null)
        if (Array.isArray(dayField)) return dayField[langIndex] ?? dayField[0] ?? "";
        return dayField ?? "";
    }

    function safeWebsiteHref(website) {
        if (!website) return "";
        return website.startsWith("http") ? website : `http://${website}`;  // Ensure URL contains protocol
    }

    function getEntityIcon(feature) {
        // Resolve appropriate icon based on the feature's entity type
        const entityType = String(feature.get("EntityType") ?? "");  // Empty value if entityType is null
        return ICON_BY_ENTITY_TYPE[entityType] ?? "";
    }

    // -----------------------------
    // Clustering Helpers
    // -----------------------------
    function getClusterEntityTypeCounts(clusteredFeatures) {
        const counts = {
            QuickDrawBranch: 0,
            AuthDealer: 0,
            AuthRepair: 0,
        };

        // Determine EntityType for each feature, update corresponding count
        clusteredFeatures.forEach((feature) => {
            const entityType = String(feature.get("EntityType") ?? "");
            counts[entityType] += 1;
        });

        return counts;
    }

    function getActiveClusterTypes(clusteredFeatures) {
        // Determine how many features of each type exist in the cluster
        const counts = getClusterEntityTypeCounts(clusteredFeatures);

        // Keep only types that are actually present, ordered by highest count first
        return Object.entries(counts)
            .filter(([, count]) => count > 0)
            .sort((a, b) => b[1] - a[1])
            .map(([entityType]) => entityType);
    }

    // -----------------------------
    // Style Helpers
    // -----------------------------
    function createMarkerIcon(iconSrc, scale = 1) {
        return new Icon({
            src: iconSrc,
            anchor: [0.5, 1],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction',
            scale: scale,
        });
    }

    // Regular feature style, not clicked
    function getSingleFeatureStyle(feature) {
        const icon = getEntityIcon(feature);

        return [
            new Style({
                image: createMarkerIcon(icon),
            }),
        ];
    }

    // Apply this style on feature click
    function getSelectedFeatureStyle(feature) {
        const icon = getEntityIcon(feature);
        const label = String(feature.get("Label") ?? "");

        return [
            // Circle around map icon
            new Style({
                image: new Circle({
                    radius: 18.0,
                    displacement: [0, 12],
                    fill: new Fill({
                        color: [247, 245, 179, 0.2]
                    }),
                    stroke: new Stroke({
                        color: [255, 102, 102, 1],
                        width: 4.0
                    }),
                }),
                zIndex: 5
            }),
            new Style({
                image: createMarkerIcon(icon),
                text: new Text({
                    text: label,
                    offsetY: 0,
                    offsetX: -20,
                    font: 'bold 16px Calibri,sans-serif',
                    fill: new Fill({
                        color: 'black',
                    }),
                    stroke: new Stroke({
                        color: 'white',
                        width: 4,
                    }),
                }),
                zIndex: 10
            }),
        ];
    }

    // -----------------------------
    // Selection State Tracking
    // -----------------------------
    let selectedFeature = null;

    function setSelectedFeature(feature) {
        selectedFeature = feature;
        vectorLayer.changed();  // Style function re-triggered
    }

    // -----------------------------
    // Cluster Styling
    // -----------------------------
    function clusterStyleFunction(feature) {
        const clusteredFeatures = feature.get('features') || [];
        const size = clusteredFeatures.length;

        // ------ Case 1: Style single feature, no clustering (size = 1) ------
        if (size === 1) {
            const originalFeature = clusteredFeatures[0];
            const isSelected = selectedFeature === originalFeature;

            if (isSelected) {
                return getSelectedFeatureStyle(originalFeature);  // Highlight style if clicked
            }

            return getSingleFeatureStyle(originalFeature);
        }

        // ------ Case 2: Clustered features of the same type ------
        const activeTypes = getActiveClusterTypes(clusteredFeatures);

        if (activeTypes.length === 1) {
            return [
                new Style({
                    image: createMarkerIcon(ICON_BY_ENTITY_TYPE[activeTypes[0]]),  // Same type, only render one icon
                    text: new Text({
                        text: String(size),
                        font: 'bold 15px Arial, sans-serif',
                        offsetX: 12,
                        offsetY: -24,
                        fill: new Fill({
                            color: '#ffffff',
                        }),
                        stroke: new Stroke({
                            color: 'rgba(0, 0, 0, 0.75)',
                            width: 4,
                        }),
                    }),
                }),
            ];
        }

        // Case 3: Mixed cluster, use generic cluster style (TBD)
        return [
            new Style({
                image: new Circle({
                    radius: 14,
                    fill: new Fill({
                        color: '#1a3f74',
                    }),
                    stroke: new Stroke({
                        color: '#ffffff',
                        width: 3,
                    }),
                }),
                text: new Text({
                    text: String(size),
                    font: 'bold 14px Arial, sans-serif',
                    textAlign: 'center',
                    textBaseline: 'middle',
                    fill: new Fill({
                        color: '#ffffff',
                    }),
                }),
            }),
        ];
    }

    // -----------------------------
    // Mapbox Info Popup Helpers
    // -----------------------------
    function buildMapboxHtml(props, geometry, langIndex) {
        // Select language labels, fallback to English
        const labels = LABELS_BY_LANG[langIndex] ?? LABELS_BY_LANG[0];

        // Location information from properties (see locations.geojson)
        const name = props.Name ?? "";
        const address = props.Address ?? "";
        const city = props.City ?? "";
        const state = props.State ?? "";
        const zip = props.Zip ?? "";

        // Contact details
        const phone = props.Phone ?? "";
        const website = props.URL ?? "";

        // Hours of operation
        const mon = getDayValue(props.Mon, langIndex);
        const tue = getDayValue(props.Tue, langIndex);
        const wed = getDayValue(props.Wed, langIndex);
        const thu = getDayValue(props.Thu, langIndex);
        const fri = getDayValue(props.Fri, langIndex);
        const sat = getDayValue(props.Sat, langIndex);
        const sun = getDayValue(props.Sun, langIndex);

        const timeZone = getDayValue(props.TimeZone, langIndex);

        const hoursTitle = timeZone
            ? `${labels.hours} (${timeZone})`
            : labels.hours;

        // Convert map projection coordinates for external Google Maps link
        const [lon, lat] = transform(geometry.getCoordinates(), "EPSG:3857", "EPSG:4326");

        const href = safeWebsiteHref(website);  // Normalize website URL

        // Build and return HTML for the info popup
        return `
            <span style="font-weight:700;">${name}</span><br>
            ${address}<br>
            <span style="font-weight:700;">${city}</span>, ${state}, ${zip}<br>
        
            <table cellspacing="0" cellpadding="0" border="0">
                <tr>
                    <td>${labels.phone}</td>
                    <td><span style="font-weight:700;">${phone}</span></td>
                </tr>
                <tr>
                    <td width="70px">${labels.website}</td>
                    <td>
                        ${website
            ? `<a href="${href}" target="_blank" rel="noopener">${website}</a>`
            : ""
        }
                    </td>
                </tr>
            </table>
            
            <span style="font-weight:700;">${hoursTitle}</span><br>
        
            <table cellspacing="0" cellpadding="0" border="0">
                <tr><td width="40px">${labels.days[0]}:</td><td>${mon}</td></tr>
                <tr><td>${labels.days[1]}:</td><td>${tue}</td></tr>
                <tr><td>${labels.days[2]}:</td><td>${wed}</td></tr>
                <tr><td>${labels.days[3]}:</td><td>${thu}</td></tr>
                <tr><td>${labels.days[4]}:</td><td>${fri}</td></tr>
                <tr><td>${labels.days[5]}:</td><td>${sat}</td></tr>
                <tr><td>${labels.days[6]}:</td><td>${sun}</td></tr>
            </table>
            
            <a href="https://maps.google.com/maps?q=loc:${lat.toFixed(8)},${lon.toFixed(8)}" target="_blank" rel="noopener"> 
                ${labels.maps}
            </a>
        `;
    }

    function showMapbox(feature) {
        // Populate and display the info popup for the selected feature
        if (!mapbox) return;

        const props = feature.getProperties();
        const geometry = feature.getGeometry();
        const langIndex = getPageLangIndex();

        mapbox.innerHTML = buildMapboxHtml(props, geometry, langIndex);
        mapbox.style.display = "block";
    }

    function hideMapbox() {
        // Clear and hide info popup
        if (!mapbox) return;
        mapbox.innerHTML = "";
        mapbox.style.display = "none";
    }

    // -----------------------------
    // Zooming & Scrolling
    // -----------------------------
    function setupZoomHintAndScrollLock() {
        const mapEl = document.getElementById("map");
        const zoomHint = document.getElementById("map-zoom-hint");

        if (!mapEl || !zoomHint) return;

        // Show hint when hovering over map
        mapEl.addEventListener("mouseenter", () => {
            zoomHint.classList.add("visible");
        });

        // Hide hint when mouse leaves map
        mapEl.addEventListener("mouseleave", () => {
            zoomHint.classList.remove("visible");
        });

        // Hide hint if Ctrl/Cmd is held (zoom mode is active)
        document.addEventListener("keydown", (e) => {
            if ((e.ctrlKey || e.metaKey) && mapEl.matches(":hover")) {
                zoomHint.classList.remove("visible");
            }
        });

        document.addEventListener("keyup", (e) => {
            if (!(e.ctrlKey || e.metaKey) && mapEl.matches(":hover")) {
                zoomHint.classList.add("visible");
            }
        });
    }

    // -----------------------------
    // OL Setup & Config
    // -----------------------------
    const source = new OSM();

    const overviewMapControl = new OverviewMap({
        layers: [
            new TileLayer({
                source: source,
            }),
        ],
    });

    const vectorSource = new VectorSource({
        url: "/data/locations.geojson",
        format: new GeoJSON(),
    });

    const clusterSource = new Cluster({
        distance: 40,
        source: vectorSource,
    });

    const vectorLayer = new VectorLayer({
        source: clusterSource,
        style: clusterStyleFunction,
        visible: true,
    });

    // Center and zoom determined based on viewport size
    let center = [-87.0, 40.75];
    let zoom = 4.7;

    if (window.innerWidth <= 899) {
        center = [-87.25, 44.50];
        zoom = 3.4;
    }

    const map = new Map({
        target: 'map',
        layers: [
            new TileLayer({
                source: new OSM(),
            }),
            vectorLayer,
        ],
        view: new View({
            center: transform(center, 'EPSG:4326', 'EPSG:3857'),
            zoom: zoom,
        }),
        controls: defaultControls({
            attributionOptions: ({
                collapsible: true
            })
        }).extend([overviewMapControl]),

        // Interaction behavior resembles Google Maps...
        interactions: defaultInteractions({
            // Disable default behaviour
            dragPan: false,
            mouseWheelZoom: false,
        }).extend([
            new DragPan({
                condition: primaryAction,  // Allow panning while Ctrl is held
            }),
            new MouseWheelZoom({
                condition: platformModifierKeyOnly,  // Zoom only while Ctrl is held
            }),
        ]),
    });

    map.on("singleclick", (e) => {
        const clusterFeature = map.forEachFeatureAtPixel(
            e.pixel,
            (f) => f,
            { hitTolerance: 3 }
        );

        // Empty map area clicked, hide popup
        if (!clusterFeature) {
            setSelectedFeature(null);
            hideMapbox();
            return;
        }

        const clusteredFeatures = clusterFeature.get('features') || [];
        const size = clusteredFeatures.length;

        // If cluster is clicked, zoom in to expand
        if (size > 1) {
            const view = map.getView();

            view.animate({
                center: [e.coordinate[0], e.coordinate[1]],
                zoom: view.getZoom() + 2,
                duration: 150,
            });

            setSelectedFeature(null);
            hideMapbox();
            return;
        }

        // Single features are treated as normal icon selection, show popup
        const originalFeature = clusteredFeatures[0];
        setSelectedFeature(originalFeature);
        showMapbox(originalFeature);
    });

    // -----------------------------
    // Initialization
    // -----------------------------
    setupZoomHintAndScrollLock();
}

document.addEventListener("DOMContentLoaded", initLocationsMap);
